import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Activity,
    ArrowRight,
    BarChart2,
    BatteryCharging,
    Database,
    LineChart,
    Shield,
} from 'lucide-react';
import { useAuth } from '../../components/router/authContext';
import { FiUser, FiLogOut } from 'react-icons/fi';
import { deriveProductState } from '../../utils/productAccess';
import { isLocal } from '../../config/env';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { WELCOME_HOME_TOUR } from '../../data/tutorialSteps';
import CaHdForecastBanner from '../../components/common/CaHdForecastBanner';

const formatPlanType = (plan) => {
    if (!plan) return null;
    return plan.charAt(0).toUpperCase() + plan.slice(1);
};

const HOME_CARDS = {
    efleetplan: {
        id: 'efleetplan',
        title: 'eFleetPlan',
        module: 'fleet',
        icon: BarChart2,
        gradient: 'from-blue-500 to-blue-700',
        accent: 'text-blue-600',
        hoverGlow: 'hover:shadow-blue-100',
        borderHover: 'hover:border-blue-200',
        desc: 'Intelligent fleet electrification planning',
        subscriptionPath: '/subscription?module=fleet',
        quickLinks: [{ label: 'Enter', path: '/fleet/dashboard', icon: BarChart2 }],
    },
    chargerplan: {
        id: 'chargerplan',
        title: 'ChargerPlan',
        module: 'charger',
        icon: BatteryCharging,
        gradient: 'from-emerald-400 to-teal-600',
        accent: 'text-green-600',
        hoverGlow: 'hover:shadow-emerald-100',
        borderHover: 'hover:border-emerald-200',
        desc: 'Strategic charging site selection',
        subscriptionPath: '/subscription?module=charger',
        quickLinks: [{ label: 'Enter', path: '/charger/dashboard', icon: BarChart2 }],
    },
    gridplan: {
        id: 'gridplan',
        title: 'GridPlan',
        module: 'utility',
        icon: Activity,
        gradient: 'from-violet-500 to-purple-700',
        accent: 'text-purple-600',
        hoverGlow: 'hover:shadow-violet-100',
        borderHover: 'hover:border-violet-200',
        desc: 'EV load forecasting and grid analysis',
        quickLinks: [{ label: 'Enter', path: '/utility/select', icon: BarChart2 }],
    },
    policyplan: {
        id: 'policyplan',
        title: 'PolicyPlan',
        module: 'regulator',
        icon: LineChart,
        gradient: 'from-orange-400 to-rose-500',
        accent: 'text-teal-600',
        hoverGlow: 'hover:shadow-orange-100',
        borderHover: 'hover:border-orange-200',
        desc: 'Data-driven analysis and decision support',
        quickLinks: [{ label: 'Enter', path: '/regulator/map', icon: LineChart }],
    },
    datahub: {
        id: 'datahub',
        title: 'Data Hub',
        icon: Database,
        gradient: 'from-amber-400 to-orange-500',
        accent: 'text-orange-600',
        hoverGlow: 'hover:shadow-amber-100',
        borderHover: 'hover:border-amber-200',
        desc: 'Critical data at one place',
        devOnly: true,
        quickLinks: [{ label: 'Enter', path: '/data', icon: Database }],
    },
    internalops: {
        id: 'internalops',
        title: 'Internal Ops',
        icon: Shield,
        gradient: 'from-slate-700 to-slate-900',
        accent: 'text-slate-700',
        hoverGlow: 'hover:shadow-slate-100',
        borderHover: 'hover:border-slate-300',
        desc: 'Usage, users, and database internals',
        quickLinks: [{ label: 'Enter', path: '/internal', icon: Shield }],
    },
};

function LoginLanding() {
    const navigate = useNavigate();
    const { currentUser, logout } = useAuth();
    const { isTourCompleted, startTour } = useTutorialContext();
    const tourStartedRef = useRef(false);
    const [verificationNotice, setVerificationNotice] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [devPanelOpen, setDevPanelOpen] = useState(false);
    const [simulateView, setSimulateView] = useState(false);
    const [devEmail, setDevEmail] = useState('');
    const [devSubscriptionType, setDevSubscriptionType] = useState('basic');
    const [devSubscriptionStatus, setDevSubscriptionStatus] = useState('active');
    const [devSignupChannel, setDevSignupChannel] = useState('standard');
    const [devEmailVerified, setDevEmailVerified] = useState(true);
    const [devPolicyDomainAllowed, setDevPolicyDomainAllowed] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleResendVerification = async () => {
        try {
            setVerificationError('');
            const token = localStorage.getItem('authToken');
            await axios.post(
                API_ENDPOINTS.resendVerificationEmail,
                {},
                { headers: { Authorization: `Token ${token}` } }
            );
            setVerificationNotice('Verification email sent. Please check your inbox.');
        } catch (error) {
            setVerificationError(error.response?.data?.error || 'Unable to resend verification email.');
        }
    };

    // Auto-start the welcome tour on first visit (after auth resolves)
    useEffect(() => {
        if (!currentUser || tourStartedRef.current) return;
        const timer = setTimeout(() => {
            if (!isTourCompleted('welcome_home')) {
                tourStartedRef.current = true;
                startTour('welcome_home', WELCOME_HOME_TOUR);
            }
        }, 800);
        return () => clearTimeout(timer);
    }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isLocal || !currentUser) return;
        setDevEmail(currentUser.email || 'tester@example.com');
        setDevSubscriptionType(currentUser.subscription_type || 'basic');
        setDevSubscriptionStatus(currentUser.subscription_status || 'active');
        setDevSignupChannel(currentUser.signup_channel || 'standard');
        setDevEmailVerified(Boolean(currentUser.email_verified));
    }, [currentUser]);

    const previewUser = isLocal && simulateView
        ? {
            ...(currentUser || {}),
            email: devEmail,
            subscription_type: devSubscriptionType,
            subscription_status: devSubscriptionStatus,
            signup_channel: devSignupChannel,
            email_verified: devEmailVerified,
            has_datahub_access: false,
            has_regulator_access: false,
            has_policy_access: false,
            entitlements: null,
            __policyDomainAllowed: devPolicyDomainAllowed,
            __devSimulateView: true,
        }
        : (currentUser || {});

    const productStates = deriveProductState(previewUser);
    const allProductIds = ['efleetplan', 'chargerplan', 'gridplan', 'policyplan', 'datahub', 'internalops'];
    const productCards = allProductIds
        .filter((productId) => productStates[productId]?.visible !== false)
        .map((productId) => HOME_CARDS[productId])
        .filter(Boolean);
    const subscriptionTypeLabel = formatPlanType(previewUser?.subscription_type);
    const planDisplay = subscriptionTypeLabel ? `${subscriptionTypeLabel} plan` : null;

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {/* Dot grid */}
            <div
                className="fixed inset-0 pointer-events-none opacity-[0.04]"
                style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #1e3a5f 1px, transparent 0)', backgroundSize: '32px 32px' }}
            />
            {/* Ambient blobs */}
            <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
            <div className="fixed bottom-0 left-0 w-[400px] h-[400px] bg-teal-100/40 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

            {/* Header */}
            <header className="relative z-10 bg-white/80 backdrop-blur-sm border-b border-slate-200 flex-shrink-0">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                    <img src="/img/logo.png" alt="AmpTrans" className="h-9 w-auto" />
                    <nav className="flex items-center gap-1">
                        <button
                            onClick={() => navigate('/profile')}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        >
                            <FiUser className="w-4 h-4" />
                            Profile
                        </button>
                        <div className="w-px h-5 bg-slate-200 mx-1" />
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <FiLogOut className="w-4 h-4" />
                            Sign out
                        </button>
                    </nav>
                </div>
            </header>

            {/* Main */}
            <main className="relative z-10 flex-grow w-full max-w-4xl mx-auto px-6 lg:px-8 py-16">
                {isLocal && (
                    <div className="mb-4 flex flex-col gap-3">
                        <div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium w-fit">
                            LOCAL DEV MODE (all modules access enabled)
                            <button
                                type="button"
                                className="underline"
                                onClick={() => setDevPanelOpen((prev) => !prev)}
                            >
                                {devPanelOpen ? 'Hide mini console' : 'Open mini console'}
                            </button>
                        </div>
                        {devPanelOpen && (
                            <div className="rounded-xl border border-blue-200 bg-white p-4 text-xs text-slate-700">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-semibold text-sm">Local mini console (view simulation)</p>
                                    <label className="inline-flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={simulateView}
                                            onChange={(e) => setSimulateView(e.target.checked)}
                                        />
                                        Enable simulation
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="flex flex-col gap-1">
                                        <span>Email</span>
                                        <input
                                            className="border rounded px-2 py-1"
                                            value={devEmail}
                                            onChange={(e) => setDevEmail(e.target.value)}
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span>Subscription Type</span>
                                        <select
                                            className="border rounded px-2 py-1"
                                            value={devSubscriptionType}
                                            onChange={(e) => setDevSubscriptionType(e.target.value)}
                                        >
                                            <option value="basic">basic</option>
                                            <option value="plus">plus</option>
                                            <option value="professional">professional</option>
                                            <option value="enterprise">enterprise</option>
                                        </select>
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span>Subscription Status</span>
                                        <select
                                            className="border rounded px-2 py-1"
                                            value={devSubscriptionStatus}
                                            onChange={(e) => setDevSubscriptionStatus(e.target.value)}
                                        >
                                            <option value="active">active</option>
                                            <option value="inactive">inactive</option>
                                            <option value="cancelled">cancelled</option>
                                        </select>
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span>Signup Channel</span>
                                        <select
                                            className="border rounded px-2 py-1"
                                            value={devSignupChannel}
                                            onChange={(e) => setDevSignupChannel(e.target.value)}
                                        >
                                            <option value="standard">standard</option>
                                            <option value="southcoast">southcoast</option>
                                        </select>
                                    </label>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-4">
                                    <label className="inline-flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={devEmailVerified}
                                            onChange={(e) => setDevEmailVerified(e.target.checked)}
                                        />
                                        Email verified
                                    </label>
                                    <label className="inline-flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={devPolicyDomainAllowed}
                                            onChange={(e) => setDevPolicyDomainAllowed(e.target.checked)}
                                        />
                                        Policy allowlist hit
                                    </label>
                                </div>
                                <p className="mt-3 text-[11px] text-slate-500">
                                    Note: When simulation is off, all modules are accessible locally by default; when simulation is on, only the main page view rules are simulated.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {!currentUser?.email_verified && (
                    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
                        <p className="font-semibold">Email verification required</p>
                        <p className="mt-1">
                            Verify your email before entering a module.
                        </p>
                        <button
                            type="button"
                            onClick={handleResendVerification}
                            className="mt-3 inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-amber-700"
                        >
                            Resend verification email
                        </button>
                        {verificationNotice && <p className="mt-2 text-emerald-700">{verificationNotice}</p>}
                        {verificationError && <p className="mt-2 text-red-700">{verificationError}</p>}
                    </div>
                )}

                {/* Welcome */}
                <div className="mb-12 text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Welcome back{currentUser?.first_name ? `, ${currentUser.first_name}` : ''}
                    </h1>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                        <p className="text-sm text-gray-400">
                            Select a module to continue your work.
                        </p>
                        {planDisplay && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white text-gray-500 text-sm font-medium border border-gray-200">
                                {planDisplay}
                            </span>
                        )}
                    </div>
                </div>

                {/* Product Cards */}
                <div className="grid sm:grid-cols-2 gap-5" data-tutorial-id="home-product-grid">
                    {productCards.map((card, index) => {
                        const state = productStates[card.id] || { hasAccess: false, label: 'Not Subscribed' };
                        const quickLinks = card.quickLinks || [];
                        const fallbackDestination = state.hasAccess
                            ? quickLinks[0]?.path
                            : (card.subscriptionPath || card.waitlistPath || '/subscription');
                        const destination = state.destination || fallbackDestination;
                        const ctaLabel = state.ctaLabel || (state.hasAccess ? 'Enter' : 'View plans');
                        const Icon = card.icon;

                        return (
                            <motion.button
                                key={card.id}
                                type="button"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.07, duration: 0.35 }}
                                onClick={() => destination && navigate(destination)}
                                className={`group block w-full rounded-2xl border border-gray-200 bg-white p-6 text-left transition-all duration-200 hover:shadow-lg ${card.hoverGlow || ''} ${card.borderHover || ''}`}
                                {...(card.id === 'efleetplan' ? { 'data-tutorial-id': 'home-efleetplan-card' } : {})}
                            >
                                <div className="flex items-center gap-4 mb-6">
                                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200`}>
                                        <Icon className="w-7 h-7 text-white" strokeWidth={1.8} />
                                    </div>
                                    <div>
                                        <div className="text-base font-semibold text-gray-900 mb-1">{card.title}</div>
                                        <div className="text-sm text-gray-500 leading-relaxed">
                                            {card.desc || card.tagline}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-400 group-hover:text-gray-700 transition-colors">
                                    <span>{ctaLabel}</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            </main>

            <CaHdForecastBanner className="relative z-10 flex-shrink-0 mt-auto" />

            {/* Footer */}
            <footer className="relative z-10 border-t border-slate-200 bg-white/60 backdrop-blur-sm flex-shrink-0">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
                    <p className="text-sm text-slate-400">© {new Date().getFullYear()} AmpTrans. All rights reserved.</p>
                    <a href="/contact" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}

export default LoginLanding;
